/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useCallback, useMemo } from 'react';

import { css, cssMap, jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import Checkbox from '@atlaskit/checkbox';
import CardClient from '@atlaskit/link-provider/client';
import { Box, Text } from '@atlaskit/primitives/compiled';
import SectionMessage from '@atlaskit/section-message';
import Select, { type ValueType } from '@atlaskit/select';
import TextArea from '@atlaskit/textarea';
import { token } from '@atlaskit/tokens';

import { messages } from '../../../common/messages';
import { getSelectedOption, type SelectOption } from '../config-panel-options';
import { FieldLabel } from '../config-panel-primitives';
import type { ManagedBlockConfigurationUpdate } from '../configuration';
import { MAX_ADDITIONAL_CONTEXT_LENGTH } from '../configuration/utils';

import type { SmartCardProviderProduct } from './smart-card-product';
import { getSourceValidationMessageKey, type SourceValidationError } from './source-validation';
import { SourceList } from './SourceList';
import type { ManagedBlockSource, ManagedBlockSourceMode } from './types';
import { useSources } from './useSources';

const sourceInputStyles = css({
	width: '100%',
	boxSizing: 'border-box',
	borderColor: token('color.border.input'),
	borderStyle: 'solid',
	borderWidth: token('border.width'),
	borderRadius: token('radius.small'),
	backgroundColor: token('elevation.surface'),
	color: token('color.text'),
	font: 'inherit',
	paddingTop: token('space.100'),
	paddingRight: token('space.100'),
	paddingBottom: token('space.100'),
	paddingLeft: token('space.100'),
	'&::placeholder': {
		color: token('color.text.subtlest'),
	},
	'&:focus': {
		outlineColor: token('color.border.focused'),
		outlineOffset: token('space.025'),
		outlineStyle: 'solid',
		outlineWidth: token('border.width.focused'),
	},
});

const sourceInputInvalidStyles = css({
	borderColor: token('color.border.danger'),
});

const errorTextStyles = css({
	color: token('color.text.danger'),
	marginTop: token('space.100'),
});

const additionalContextStyles = cssMap({
	sourceDisclaimer: {
		marginTop: token('space.100'),
	},
	root: {
		marginTop: token('space.100'),
	},
	textArea: {
		marginTop: token('space.075'),
	},
	message: {
		marginTop: token('space.050'),
	},
});

type SourcesPanelProps = {
	additionalContext: string;
	additionalContextValidationError: boolean;
	isAdditionalContextSelected: boolean;
	isDisabled?: boolean;
	onChange: (update: ManagedBlockConfigurationUpdate) => void;
	onConfigEdit: () => void;
	onInteraction: () => void;
	showInitialSourceDisclaimer?: boolean;
	smartCardProviderProduct?: SmartCardProviderProduct;
	sourceEntries: ManagedBlockSource[];
	sourceMode: ManagedBlockSourceMode;
	sourceModeOptions: SelectOption<ManagedBlockSourceMode>[];
	sourceUrls: string;
	sourceValidationError: SourceValidationError;
};

export const SourcesPanel = ({
	additionalContext,
	additionalContextValidationError,
	isDisabled = false,
	isAdditionalContextSelected,
	onChange,
	onConfigEdit,
	onInteraction,
	sourceEntries,
	sourceMode,
	sourceModeOptions,
	showInitialSourceDisclaimer,
	smartCardProviderProduct,
	sourceUrls,
	sourceValidationError,
}: SourcesPanelProps): JSX.Element => {
	const intl = useIntl();
	const cardClient = useMemo(() => new CardClient(), []);
	const {
		handleDismissSourceDisclaimer,
		isSourceDisclaimerVisible,
		manualSources,
		sourceInputValue,
		handleRemoveSourceUrl,
		handleRetrySourceUrl,
		handleSourceInputChange,
		handleSourceInputKeyDown,
		handleSourceInputPaste,
		handleSourceModeChange,
		showSourceLimitError,
	} = useSources({
		cardClient,
		onChange,
		onConfigEdit,
		onInteraction,
		showInitialSourceDisclaimer,
		sourceEntries,
		sourceMode,
		sourceUrls,
	});
	const sourceErrorMessageKey = showSourceLimitError
		? 'sourceLimitError'
		: getSourceValidationMessageKey(sourceValidationError);
	const sourceErrorId = sourceErrorMessageKey ? 'agent-managed-extension-source-error' : undefined;
	const additionalContextErrorId = additionalContextValidationError
		? 'agent-managed-extension-additional-context-error'
		: undefined;
	const additionalContextCounterId = 'agent-managed-extension-additional-context-counter';
	const additionalContextDescriptionIds = [additionalContextErrorId, additionalContextCounterId]
		.filter(Boolean)
		.join(' ');
	const additionalContextCharactersRemaining = Math.max(
		0,
		MAX_ADDITIONAL_CONTEXT_LENGTH - additionalContext.length,
	);

	const handleAdditionalContextChange = useCallback(
		(event: React.ChangeEvent<HTMLTextAreaElement>): void => {
			const nextAdditionalContext = event.currentTarget.value.slice(
				0,
				MAX_ADDITIONAL_CONTEXT_LENGTH,
			);

			onInteraction();

			if (additionalContext !== nextAdditionalContext) {
				onConfigEdit();
			}

			onChange((currentConfiguration) => ({
				...currentConfiguration,
				additionalContext: nextAdditionalContext,
			}));
		},
		[additionalContext, onChange, onConfigEdit, onInteraction],
	);

	const handleAdditionalContextSelectedChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>): void => {
			const nextIsAdditionalContextSelected = event.currentTarget.checked;

			onInteraction();

			if (isAdditionalContextSelected !== nextIsAdditionalContextSelected) {
				onConfigEdit();
			}

			onChange((currentConfiguration) => ({
				...currentConfiguration,
				isAdditionalContextSelected: nextIsAdditionalContextSelected,
			}));
		},
		[isAdditionalContextSelected, onChange, onConfigEdit, onInteraction],
	);

	return (
		<React.Fragment>
			<div>
				<FieldLabel htmlFor="agent-managed-extension-source-mode">
					{intl.formatMessage(messages.sourcesLabel)}
				</FieldLabel>
				<Select<SelectOption<ManagedBlockSourceMode>>
					inputId="agent-managed-extension-source-mode"
					options={sourceModeOptions}
					value={getSelectedOption(sourceModeOptions, sourceMode)}
					onChange={(option: ValueType<SelectOption<ManagedBlockSourceMode>>) => {
						if (option) {
							handleSourceModeChange(option.value);
						}
					}}
					isDisabled={isDisabled}
					isSearchable={false}
				/>
			</div>
			{sourceMode === 'manual' && (
				<div>
					<input
						aria-describedby={sourceErrorId}
						aria-invalid={Boolean(sourceErrorMessageKey)}
						css={[sourceInputStyles, sourceErrorMessageKey && sourceInputInvalidStyles]}
						data-testid="agent-managed-extension-source-input"
						disabled={isDisabled}
						onChange={handleSourceInputChange}
						onKeyDown={handleSourceInputKeyDown}
						onPaste={handleSourceInputPaste}
						placeholder={intl.formatMessage(messages.sourcesPlaceholder)}
						type="text"
						value={sourceInputValue}
					/>
					<SourceList
						aria-label={intl.formatMessage(messages.sourceLinksLabel)}
						cardClient={cardClient}
						isDisabled={isDisabled}
						onRemove={handleRemoveSourceUrl}
						onRetry={handleRetrySourceUrl}
						smartCardProviderProduct={smartCardProviderProduct}
						sources={manualSources}
					/>
					{sourceErrorMessageKey && (
						<div css={errorTextStyles} id={sourceErrorId} role="alert">
							<Text color="color.text.danger">
								{intl.formatMessage(messages[sourceErrorMessageKey])}
							</Text>
						</div>
					)}
				</div>
			)}
			{isSourceDisclaimerVisible && (
				<Box xcss={additionalContextStyles.sourceDisclaimer}>
					<SectionMessage
						appearance="information"
						isDismissible={!isDisabled}
						onDismiss={handleDismissSourceDisclaimer}
						testId="agent-managed-extension-source-disclaimer"
					>
						{intl.formatMessage(messages.sourceDisclaimerCopy)}
					</SectionMessage>
				</Box>
			)}
			<Box xcss={additionalContextStyles.root}>
				<Checkbox
					id="agent-managed-extension-additional-context-toggle"
					isChecked={isAdditionalContextSelected}
					isDisabled={isDisabled}
					label={intl.formatMessage(messages.additionalContextLabel)}
					onChange={handleAdditionalContextSelectedChange}
				/>
				{isAdditionalContextSelected && (
					<Box xcss={additionalContextStyles.textArea}>
						<TextArea
							aria-describedby={additionalContextDescriptionIds}
							aria-label={intl.formatMessage(messages.additionalContextDetailsLabel)}
							id="agent-managed-extension-additional-context"
							isDisabled={isDisabled}
							isInvalid={additionalContextValidationError}
							maxLength={MAX_ADDITIONAL_CONTEXT_LENGTH}
							minimumRows={3}
							onChange={handleAdditionalContextChange}
							placeholder={intl.formatMessage(messages.additionalContextPlaceholder)}
							testId="agent-managed-extension-additional-context"
							value={additionalContext}
						/>
						{additionalContextValidationError && (
							<Box
								id={additionalContextErrorId}
								role="alert"
								xcss={additionalContextStyles.message}
							>
								<Text color="color.text.danger">
									{intl.formatMessage(messages.additionalContextRequiredError)}
								</Text>
							</Box>
						)}
						<Box id={additionalContextCounterId} xcss={additionalContextStyles.message}>
							<Text color="color.text.subtle">
								{intl.formatMessage(messages.additionalContextCharactersRemaining, {
									remaining: additionalContextCharactersRemaining,
								})}
							</Text>
						</Box>
					</Box>
				)}
			</Box>
		</React.Fragment>
	);
};
